/*
 * Copyright (C) 2025 SimpliCity Digital Inc - All Rights Reserved
 */

import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { SimpliCitySDK } from "cms/sdk";
import { useAuth } from "cms/auth/hooks/useAuth";
import { formatDate, getMonthAndDay } from "cms/utils/format-date";

// FIXME: Site specific item
const LANGLEY_DEFAULT_NEWS_IMAGE_GUID = "d9b66c67-9ce0-4c6b-9e91-ab7c37fbd302";

const NewsAndEventsFeedComponent = (props) => {
    const component = props?.component;
    const newsLinkGuid = component?.news_link;
    const eventsLinkGuid = component?.events_link;
    const model_selected_type = component?.model_selected_type;
    const model_selected_type_2 = component?.model_selected_type_2;
    const model_selected_type_title = component?.model_selected_type_title;
    const model_selected_type_2_title = component?.model_selected_type_2_title;

    const navigate = useNavigate();
    const { token } = useAuth();
    const [newsLink, setNewsLink] = useState(null);
    const [eventsLink, setEventsLink] = useState(null);
    const [news, setNews] = useState([]);
    const [events, setEvents] = useState([]);

    const [newsBannerImage, setNewsBannerImage] = useState(null);

    useEffect(() => {
        const fetchLinks = async () => {
            if (newsLinkGuid && typeof newsLinkGuid === "string" && newsLinkGuid.length > 20) {
                try {
                    const link = await SimpliCitySDK.link.findOne(token, newsLinkGuid);
                    setNewsLink(link);
                } catch (e) {
                    setNewsLink(null);
                }
            } else {
                setNewsLink(null);
            }

            if (eventsLinkGuid && typeof eventsLinkGuid === "string" && eventsLinkGuid.length > 20) {
                try {
                    const link = await SimpliCitySDK.link.findOne(token, eventsLinkGuid);
                    setEventsLink(link);
                } catch (e) {
                    setEventsLink(null);
                }
            } else {
                setEventsLink(null);
            }
        };
        fetchLinks();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [newsLinkGuid, eventsLinkGuid, token]);

    useEffect(() => {
        const loadNews = async () => {
            const options = {
                type: model_selected_type,
                pageSize: 5,
                pageLimit: 1,
                selected_category: "All",
                categoryName: "category",
            };
            const news = await SimpliCitySDK.model.findFeed(token, options);
            if (news.results.length > 0) {
                setNews(news.results);
                const banner_image_guid = news.results[0]?.banner_image?.image || LANGLEY_DEFAULT_NEWS_IMAGE_GUID;
                await SimpliCitySDK.image.findOne(token, banner_image_guid)
                    .then((image) => {
                        setNewsBannerImage(image);
                    })
                    .catch(() => {
                        setNewsBannerImage(null);
                    });
            } else {
                setNews([]);
            }
        };
        loadNews();
    }, [model_selected_type, token]);

    useEffect(() => {
        const loadEvents = async () => {
            const options = {
                type: model_selected_type_2,
                pageSize: 4,
                pageLimit: 1,
                selected_category: "All",
                categoryName: "category",
            };
            const events = await SimpliCitySDK.model.findEventFeed(token, options);
            if (events.results.length > 0) {
                setEvents(events.results);
            } else {
                setEvents([]);
            }
        };
        loadEvents();
    }, [model_selected_type_2, token]);

    const gotoNews = (index) => {
        if (news && news.length > 0 && news[index]?.path) {
            navigate(news[index].path);
        }
    };

    const gotoEvent = (index) => {
        if (events && events.length > 0 && events[index]?.path) {
            navigate(events[index].path);
        }
    };

    const gotoFullNews = () => {
        if (newsLink?.url) {
            const url = new URL(newsLink.url, window.location.origin);
            navigate(url.pathname);
        }
    };

    const gotoFullEvents = () => {
        if (eventsLink?.url) {
            const url = new URL(eventsLink.url, window.location.origin);
            navigate(url.pathname);
        }
    };

    return (
        <div className='simplicity-news-event-feed-display-wrapper'>
            <div className='section-one'>
                <div className='news-headline'>{model_selected_type_title}</div>
                <div className='news-wrapper'>
                    <div className='news-item-first'>
                        <div className='news-item-first-item' onClick={() => gotoNews(0)}>
                            <div className='news-item-first-image'>{newsBannerImage && <img src={newsBannerImage?.url} alt="News banner"/>}</div>
                            <div className='news-item-first-info'>
                                <div className='news-item-first-date'>{formatDate(new Date(news[0]?.created_at),"MMM D, YYYY")}</div>
                                <div className='news-item-first-title'>{news[0]?.title}</div>
                            </div>
                        </div>
                    </div>
                    <div className='news-item-all'>
                        {news.slice(1).map((item, index) => (
                            <div key={index} className='news-item' onClick={() => gotoNews(index + 1)}>
                                <div className='news-item-date'>{formatDate(new Date(item?.created_at),"MMM D, YYYY")}</div>
                                <div className='news-item-title'>{item.title}</div>
                            </div>
                        ))}
                    </div>
                </div>
                <div className='go-to-news' onClick={gotoFullNews}>{newsLink?.text}</div>
            </div>
            <div className='section-two'>
                <div className='event-headline'>{model_selected_type_2_title}</div>
                {events.length > 0 ? (
                    events.map((item, index) => {
                        const eventTitle = item?.event?.Title;
                        const eventStartTime = item?.event?.StartTime;
                        const eventEndTime = item?.event?.EndTime;
                        const eventStartDate = getMonthAndDay(item?.event?.StartDate);
                        return (
                            <div key={index} className={`event-item ${index === 0 ? "event-item-first" : ""}`} onClick={() => gotoEvent(index)}>
                                <div className='event-date'>
                                    <div className='event-date-month'>{eventStartDate?.month}</div>
                                    <div className='event-date-day'>{eventStartDate?.day}</div>
                                </div>
                                <div className='event-info'>
                                    <div className='event-title'>{eventTitle}</div>
                                    <div className='event-time'>{eventStartTime}{eventEndTime &&  `- ${eventEndTime}`}</div>
                                </div>
                            </div>
                        );

                    })
                ) : (
                    <p>No events available.</p>
                )}
                <div className='go-to-events' onClick={gotoFullEvents}>{eventsLink?.text}</div>
            </div>
        </div>
    );
};

NewsAndEventsFeedComponent.displayName = "NewsAndEventsFeedComponent";
export { NewsAndEventsFeedComponent };
